import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EnvironmentActions, PaymentActions } from '@app/core/actions';
import { CookiesService } from '@app/core/services/cookies.service';
import { IamService } from '@app/core/services/iam.service';
import { RedirectionService } from '@app/core/services/redirection.service';
import { TokenService } from '@app/core/services/token.service';
import * as fromRoot from '@app/reducers';
import { computePriceInclTax } from '@app/shared/helpers/vat';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, retryWhen, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromSignup from '../../reducers';
import { SubscriptionActions } from '../actions';
import { config } from '@smash-sdk/core';
import { Directory } from '@smash-sdk/directory/ejs/11-2023';
import { Language } from '@app/shared/types/Language';
import { Billing } from '@smash-sdk/billing/ejs/01-2024/billing';
import UpdateInformationsError from '@smash-sdk/billing/ejs/01-2024/types/UpdateInformations/UpdateInformationsError';
import { cleanEmptyValuesFromBody } from '@app/shared/helpers/helpers';

@Injectable()
export class SubscriptionEffects {

  loadPlans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.loadPlans),
      switchMap((action) => {
        const planIdOrGroupId = action.id;
        const billingSdk = new Billing();
        if (planIdOrGroupId.length !== Constant.planGroupIdLength) {
          return from(billingSdk.getGroup({ groupId: planIdOrGroupId })).pipe(
            map((res: any) => {
              const plans = res.group.plans;
              return SubscriptionActions.loadPlansSuccess({ plans });
            }),
            catchError(error =>
              of(SubscriptionActions.loadPlansFailure({ error: error.message }))
            )
          );
        } else if (planIdOrGroupId.length === Constant.planGroupIdLength) {
          return from(billingSdk.getPlanGroup({ planId: planIdOrGroupId })).pipe(
            map((res) => {
              const plans = res.group.plans;
              return SubscriptionActions.loadPlansSuccess({ plans });
            }),
            catchError(error =>
              of(SubscriptionActions.loadPlansFailure({ error: error.message }))
            )
          );
        } else {
          return of(SubscriptionActions.loadPlansFailure({ error: 'PlanId didn\'t match any pattern' }));
        }
      })
    )
  );

  loadPlansFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.loadPlansFailure),
      mergeMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store$.select(fromRoot.getSelectedLanguage),
          )
        )
      ),
      tap(([action, language]: any[]) => {
        this.redirectionService.redirectToPricingPage(language);
      })
    ),
    { dispatch: false }
  );

  computePlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.computePlan),
      mergeMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store$.select(fromSignup.getFormData),
          )
        )
      ),
      switchMap(([action, formData]) => {
        const { plan, country, vatNumber } = formData;
        if (plan && country) {
          const computedPlan = computePriceInclTax(plan, country, vatNumber);
          return of(SubscriptionActions.computePlanSuccess({ computedPlan: { ...computedPlan, duration: plan.duration } }));
        } else {
          return of(SubscriptionActions.computePlanFailure({ error: 'Can\'t compute plan priceExclTax' }));
        }
      })
    )
  );

  signup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.signup),
      mergeMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store$.select(fromRoot.getToken),
            this.store$.select(fromRoot.getSubscription),
          )
        )
      ),
      mergeMap(([action, token, subscription]: any[]) => {
        if (token) {
          if (this.tokenService.isAnonymousAccount(token)) {
            // Updating account to premium
            return [
              SubscriptionActions.updateAccount(),
            ];
          } else {
            // Account has a subscription and a plan
            if (subscription && subscription.plan) {
              if (subscription.plan.id === Constant.planFreemiumID) {
                return [SubscriptionActions.updateBillingInformations()];
              }
              // Payment required
              if (subscription.status === Constant.subscriptionStatus.PaymentRequired) {
                return [
                  SubscriptionActions.updateBillingInformations(),
                ];
              } else if (subscription.status === Constant.subscriptionStatus.Active && subscription.automaticRenew === false) {
                return [
                  SubscriptionActions.updateBillingInformations(),
                ];
              }
            } else {
              // Account has no subscription
              return [
                SubscriptionActions.updateBillingInformations(),
              ];
            }
          }
        }
      })
    )
  );

  updateAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.updateAccount),
      mergeMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store$.select(fromRoot.getActiveIdentity),
            this.store$.select(fromSignup.getFormData),
            this.store$.select(fromRoot.getSelectedLanguage)
          )
        )
      ),
      switchMap(([action, identity, formData, language]) => {
        const accountDataUpdated = {
          username: formData.email.toLowerCase(),
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          language
        };
        return this.iamService.updateAccount(accountDataUpdated).pipe(
          mergeMap(() => {
            const directorySdk = new Directory();
            return from(directorySdk.createToken(accountDataUpdated)).pipe(
              mergeMap(({ identity }) => {
                this.cookiesService.setCookie('identity', identity);
                config.setToken(identity.token.token);
                const preselectedIdentity = identity;
                const userLanguage = preselectedIdentity?.language as Language || language;
                const _identity = {
                  ...preselectedIdentity,
                  isRoot: this.tokenService.isRoot(preselectedIdentity.token.token)
                }
                return [
                  EnvironmentActions.SelectLanguage({ language: userLanguage }),
                  SubscriptionActions.updateAccountSuccess({ identity: _identity }),
                  SubscriptionActions.updateBillingInformations(),
                ];
              }));
          }),
          retryWhen(error =>
            error.pipe(
              take(3),
              map((err: HttpErrorResponse) => {
                if (!(err.status >= 500)) {
                  throw err;
                }
              })
            )
          ),
          catchError((error: HttpErrorResponse) => {
            switch (error.status) {
              case 404:
                // TODO Handle changement region 404 on getActiveIdentity, recreate new anonymous user and update account
                return of(SubscriptionActions.updateAccountFailure({ error: error.message }));
              case 409:
                return of(SubscriptionActions.updateAccountFailure({ error: 'EMAIL_ALREADY_EXISTS' }));
              default:
                return of(SubscriptionActions.updateAccountFailure({ error: error.message }));
            }
          },
          )
        );
      })
    )
  );

  updateBillingInformations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.updateBillingInformations),
      mergeMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store$.select(fromSignup.getFormData)
          )
        )
      ),
      switchMap(([action, formData]) => {
        const billingInformations = {
          plan: formData.plan,
          company: formData.company,
          description: formData.description,
          address: formData.address,
          zipCode: formData.zipCode?.trim(),
          city: formData.city,
          vatNumber: formData.vatNumber?.replace(/\s+/g, ''),
          firstName: formData.firstName,
          lastName: formData.lastName,
          country: formData.country.label,
          countryCode: formData.country.countryCode,
          state: formData.state?.label,
          stateCode: formData.state?.stateCode,
        };
        const billingSdk = new Billing();


        // Temp FIx clean empty fields with cleanEmptyValuesFromBody
        return from(billingSdk.updateInformations(cleanEmptyValuesFromBody(billingInformations))).pipe(
          mergeMap(({ informations }) => {
            return [
              SubscriptionActions.updateBillingInformationsSuccess({ billingInformations: informations }),
              SubscriptionActions.updateSubscription(),
            ];
          }),
          catchError((error: any) => {
            if (error instanceof UpdateInformationsError.InvalidVatNumberError) {
              // FIX ME : Handle VAT number error when Billing SDK will implement error.name === 'InvalidVatNumberError'
              return of(SubscriptionActions.updateBillingInformationsFailure({ error: 'vatNumber' }));
            } else {
              return of(SubscriptionActions.updateBillingInformationsFailure({ error: error.message }));
            }
          })
        );
      })
    )
  );

  updateSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubscriptionActions.updateSubscription),
      mergeMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store$.select(fromSignup.getFormData)
          )
        )
      ),
      switchMap(([action, formData]) => {
        const billingSdk = new Billing();
        return from(billingSdk.createSubscription({ plan: formData.plan.id })).pipe(
          mergeMap((resSubscription: any) => {
            const { subscription } = resSubscription;
            return [
              SubscriptionActions.updateSubscriptionSuccess({ subscription }),
              PaymentActions.processPayment()
            ];
          }),
          catchError((error: any) => {
            return of(SubscriptionActions.updateSubscriptionFailure({ error: error.message }));
          })
        );
      })
    )
  );

  processPaymentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentActions.processPaymentSuccess),
      map(action => action),
      tap(() => {
        this.store$.dispatch(SubscriptionActions.signupSuccess());
        this.router.navigateByUrl('/signup/domain');
      })
    ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private iamService: IamService,
    private readonly router: Router,
    private store$: Store<fromRoot.State & fromSignup.State>,
    private tokenService: TokenService,
    private redirectionService: RedirectionService,
    private cookiesService: CookiesService,
  ) { }

}
